"use client";

// TODO:
// - Add Player Skeleton/loading state
// - Add analytics/logging
// - Add HI styling

import type { ComponentProps, SyntheticEvent } from "react";

import {
  trackVideoEvent,
  trackVideoThumbnailClickEvent,
} from "@homeinstead/analytics";
import { useRef } from "react";
import ReactPlayer from "react-player";
import { cn } from "../../utils/classnames";

/**
 * Type for ReactPlayer instance with its available methods.
 * Note: ReactPlayer doesn't export proper TypeScript types for its instance,
 * so we define the shape we need here for type safety.
 */
interface ReactPlayerInstance {
  getInternalPlayer: () => HTMLVideoElement;
  // Add other methods as needed:
  // seekTo: (amount: number, type?: 'seconds' | 'fraction') => void;
  // getCurrentTime: () => number;
  // getDuration: () => number;
}

type VideoPlayerEvent = SyntheticEvent<HTMLVideoElement>;

type VideoPlayerProps = Omit<
  ComponentProps<typeof ReactPlayer>,
  "onEnded" | "onReady" | "onClickPreview"
> & {
  onEnded?: (player: HTMLVideoElement | null) => void;
  onReady?: (player: HTMLVideoElement | null) => void;
  onClickPreview?: (event: SyntheticEvent<Element, Event>) => void;
  // Add these for tracking
  contentAssetId?: string;
  contentPodId?: string;
  sessionId?: string;
};

/**
 * VideoPlayer component
 * @implements ReactPlayer
 * @see https://github.com/cookpete/react-player/blob/e09d35f591551ca0eb7895d5b15dbb489d732cdd/README.md
 *
 * @note The player defaults to 16/9 aspect ratio. If a different aspect ratio is needed, pass in a
 *       custom `aspect` className
 * @see https://tailwindcss.com/docs/aspect-ratio
 */
function VideoPlayer({
  className,
  contentAssetId,
  contentPodId,
  sessionId,
  onEnded,
  onError,
  onPause,
  onPlay,
  onReady,
  onSeeked,
  onWaiting,
  onClickPreview,
  ...playerProps
}: VideoPlayerProps) {
  // Note: If we event host our own videos, we should extend this component with `next-video`,
  // which supports local video optimization.
  // See: https://github.com/muxinc/next-video?tab=readme-ov-file#custom-player-demo

  const playerRef = useRef<ReactPlayerInstance | null>(null);

  const baseTrackingProps = {
    content_asset_id: contentAssetId || "",
    content_pod_id: contentPodId,
    session_id: sessionId,
  };

  /**
   * Helper to safely access the internal player and execute a callback
   * Centralizes player access and null checks
   */
  function withInternalPlayer(
    callback: (player: HTMLVideoElement | null) => void,
  ): HTMLVideoElement | null {
    const internalPlayer = playerRef.current?.getInternalPlayer
      ? (playerRef.current.getInternalPlayer() as HTMLVideoElement | null)
      : null;
    callback(internalPlayer);
    return internalPlayer;
  }

  function handleEnded() {
    withInternalPlayer((player) => {
      trackVideoEvent("Video Playback Completed", player, baseTrackingProps);
      onEnded?.(player);
    });
  }

  function handleError(event: VideoPlayerEvent) {
    withInternalPlayer((player) => {
      trackVideoEvent("Video Playback Interrupted", player, baseTrackingProps);
    });
    onError?.(event);
  }

  function handlePause(event: VideoPlayerEvent) {
    withInternalPlayer((player) => {
      trackVideoEvent("Video Playback Paused", player, baseTrackingProps);
    });
    onPause?.(event);
  }

  function handlePlay(event: VideoPlayerEvent) {
    withInternalPlayer((player) => {
      trackVideoEvent("Video Playback Started", player, baseTrackingProps);
    });
    onPlay?.(event);
  }

  function handleReady() {
    const player = withInternalPlayer((player) => {
      trackVideoEvent("Video Content Started", player, baseTrackingProps);
    });
    onReady?.(player);
  }

  function handleSeeked(event: VideoPlayerEvent) {
    withInternalPlayer((player) => {
      trackVideoEvent(
        "Video Playback Seek Completed",
        player,
        baseTrackingProps,
      );
    });
    onSeeked?.(event);
  }

  function handleWaiting(event: VideoPlayerEvent) {
    withInternalPlayer((player) => {
      trackVideoEvent(
        "Video Playback Buffer Started",
        player,
        baseTrackingProps,
      );
    });
    onWaiting?.(event);
  }

  function handleClickPreview(event: SyntheticEvent<Element, Event>) {
    // Track thumbnail click
    // Note: url might be in playerProps but TypeScript doesn't know about it
    // from the Omit type. We'll use contentAssetId which should contain the URL as fallback
    trackVideoThumbnailClickEvent({
      video_id: contentAssetId,
      video_url: contentAssetId, // contentAssetId is set to the URL in Video.tsx
    });

    // Call custom handler if provided
    onClickPreview?.(event);
  }

  const hasCustomAspectClass = /\baspect-/.test(className ?? "");

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-sm",
        { "aspect-video": !hasCustomAspectClass },
        className,
      )}
    >
      <ReactPlayer
        {...playerProps}
        className="video-player"
        height="100%"
        onClickPreview={handleClickPreview}
        onEnded={handleEnded}
        onError={handleError}
        onPause={handlePause}
        onPlay={handlePlay}
        onReady={handleReady}
        onSeeked={handleSeeked}
        onWaiting={handleWaiting}
        ref={playerRef as React.Ref<HTMLVideoElement>}
        width="100%"
      />
    </div>
  );
}

export type { VideoPlayerEvent, VideoPlayerProps };

export default VideoPlayer;
